import { useState, useEffect } from 'react';
import { api, getCurrentLocale } from '../services/api';

export interface TreatmentType {
  id: number;
  documentId: string;
  name: string;
  slug?: string;
  shortDescription?: string;
  detailedDescription?: string;
  suitableFor?: string;
  order: number;
  isActive?: boolean;
  ingredients?: Array<{
    id: number;
    text: string;
  }>;
  benefits?: Array<{
    id: number;
    text: string;
  }>;
  image?: {
    id: number;
    documentId: string;
    name: string;
    url: string;
    alternativeText?: string;
    caption?: string;
    width: number;
    height: number;
    formats?: {
      large?: { url: string; width: number; height: number; size: number; };
      medium?: { url: string; width: number; height: number; size: number; };
      small?: { url: string; width: number; height: number; size: number; };
      thumbnail?: { url: string; width: number; height: number; size: number; };
    };
    hash: string;
    ext: string;
    mime: string;
    size: number;
    previewUrl?: string;
    provider: string;
    provider_metadata?: any;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
  ctaButton?: {
    id: number;
    text: string;
    url: string;
    variant: string;
    isExternal: boolean;
  };
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface TreatmentTypesResponse {
  data: TreatmentType[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export const useTreatmentTypes = () => {
  const [data, setData] = useState<TreatmentType[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const locale = typeof window !== 'undefined' ? getCurrentLocale() : 'nl';
    const fetchTreatmentTypes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get('/treatment-types?populate=*&sort=order:asc');
        
        // Handle Strapi Cloud structure: response.data.data contains the actual content
        let treatmentTypes: TreatmentType[] = [];
        
        if (response.data && response.data.data) {
          if (Array.isArray(response.data.data)) {
            treatmentTypes = response.data.data;
          } else {
            treatmentTypes = [response.data.data];
          }
        } else if (Array.isArray(response.data)) {
          treatmentTypes = response.data;
        }
        
        setData(treatmentTypes);
      } catch (err) {
        console.error('Error fetching treatment types:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch treatment types');
      } finally {
        setLoading(false);
      }
    };

    fetchTreatmentTypes();
  }, [typeof window !== 'undefined' ? getCurrentLocale() : 'nl']);

  return { data, loading, error };
};
